import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, ArrowUpRight, Check } from "lucide-react";
import { CONTACT, EVENT, LOGO_URL, PDF_BG } from "../data/content";
import SectionBg from "./SectionBg";

export function Contact() {
    const [form, setForm] = useState({ name: "", email: "", company: "", interest: "Attendee", message: "" });
    const [status, setStatus] = useState("idle"); // idle | success

    const submit = (e) => {
        e.preventDefault();

        const subject = `Jaipur Ad Fest enquiry - ${form.interest}`;
        const body = [
            "New Jaipur Ad Fest enquiry",
            "",
            `Name: ${form.name}`,
            `Email: ${form.email}`,
            `Company / Brand: ${form.company || "-"}`,
            `Interest: ${form.interest}`,
            "",
            "Message:",
            form.message || "-",
        ].join("\n");

        window.location.href = `mailto:${CONTACT.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        setStatus("success");
        setForm({ name: "", email: "", company: "", interest: "Attendee", message: "" });
        setTimeout(() => setStatus("idle"), 4500);
    };

    const interests = ["Attendee", "Speaker", "Sponsor", "Creator", "Press"];

    return (
        <section
            id="contact"
            className="relative py-24 md:py-40 overflow-hidden"
            data-testid="contact-section"
        >
            <SectionBg src={PDF_BG.contact} opacity={0.6} />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,107,53,0.15)_0%,transparent_50%)]" />

            <div className="max-w-[1440px] mx-auto w-full px-4 sm:px-6 md:px-12 relative">
                <div className="grid grid-cols-12 gap-6 md:gap-12 items-start">
                    {/* Left */}
                    <div className="col-span-12 lg:col-span-5 min-w-0">
                        <div className="text-xs uppercase tracking-[0.18em] sm:tracking-[0.3em] text-[#FF6B35] font-mono mb-5 sm:mb-6">
                            Get In Touch
                        </div>
                        <h2 className="min-w-0 max-w-full font-display font-bold text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tight lg:text-6xl mb-6 md:mb-8">
                            Let's make <span className="gradient-text">noise</span>.
                        </h2>
                        <p className="text-zinc-300 leading-relaxed text-sm sm:text-base md:text-lg mb-8 md:mb-10 [overflow-wrap:anywhere]">
                            Whether you want to attend, speak, sponsor or just say hi — drop us a note. We respond
                            within 48 hours.
                        </p>

                        <div className="space-y-4 sm:space-y-5 max-w-full">
                            <a
                                href={`mailto:${CONTACT.email}`}
                                data-testid="contact-email"
                                className="group flex min-w-0 max-w-full items-center gap-3 sm:gap-4 text-zinc-200 hover:text-[#FF6B35] transition"
                            >
                                <span className="h-10 w-10 shrink-0 rounded-xl border border-white/10 flex items-center justify-center group-hover:bg-[#FF6B35] group-hover:text-black group-hover:border-[#FF6B35] transition sm:h-11 sm:w-11">
                                    <Mail size={18} />
                                </span>
                                <span className="min-w-0 max-w-full break-all font-mono text-xs sm:text-sm">{CONTACT.email}</span>
                            </a>
                            <a
                                href={`tel:${CONTACT.phone}`}
                                data-testid="contact-phone"
                                className="group flex min-w-0 max-w-full items-center gap-3 sm:gap-4 text-zinc-200 hover:text-[#FF6B35] transition"
                            >
                                <span className="h-10 w-10 shrink-0 rounded-xl border border-white/10 flex items-center justify-center group-hover:bg-[#FF6B35] group-hover:text-black group-hover:border-[#FF6B35] transition sm:h-11 sm:w-11">
                                    <Phone size={18} />
                                </span>
                                <span className="min-w-0 max-w-full font-mono text-xs sm:text-sm">{CONTACT.phone}</span>
                            </a>
                            <div className="flex min-w-0 max-w-full items-start gap-3 sm:gap-4 text-zinc-300">
                                <span className="h-10 w-10 shrink-0 rounded-xl border border-white/10 flex items-center justify-center sm:h-11 sm:w-11">
                                    <MapPin size={18} />
                                </span>
                                <span className="min-w-0 max-w-full font-mono text-xs sm:text-sm leading-relaxed [overflow-wrap:anywhere]">{CONTACT.address}</span>
                            </div>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="col-span-12 lg:col-span-7 min-w-0">
                        <motion.form
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            onSubmit={submit}
                            className="w-full max-w-full overflow-hidden rounded-3xl border border-white/10 bg-[#141414] p-4 sm:p-8 md:p-12"
                            data-testid="contact-form"
                        >
                            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
                                <div className="min-w-0">
                                    <label className="text-xs uppercase tracking-[0.16em] sm:tracking-[0.25em] text-zinc-500 font-mono mb-2 block">
                                        Name
                                    </label>
                                    <input
                                        required
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        placeholder="Your name"
                                        data-testid="form-name"
                                        className="w-full min-w-0 bg-transparent border-b border-white/15 focus:border-[#FF6B35] outline-none py-3 text-sm sm:text-base text-white placeholder:text-zinc-600 transition"
                                    />
                                </div>
                                <div className="min-w-0">
                                    <label className="text-xs uppercase tracking-[0.16em] sm:tracking-[0.25em] text-zinc-500 font-mono mb-2 block">
                                        Email
                                    </label>
                                    <input
                                        required
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        placeholder="you@brand.com"
                                        data-testid="form-email"
                                        className="w-full min-w-0 bg-transparent border-b border-white/15 focus:border-[#FF6B35] outline-none py-3 text-sm sm:text-base text-white placeholder:text-zinc-600 transition"
                                    />
                                </div>
                                <div className="min-w-0 md:col-span-2">
                                    <label className="text-xs uppercase tracking-[0.16em] sm:tracking-[0.25em] text-zinc-500 font-mono mb-2 block">
                                        Company / Brand
                                    </label>
                                    <input
                                        value={form.company}
                                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                                        placeholder="Where do you work?"
                                        data-testid="form-company"
                                        className="w-full min-w-0 bg-transparent border-b border-white/15 focus:border-[#FF6B35] outline-none py-3 text-sm sm:text-base text-white placeholder:text-zinc-600 transition"
                                    />
                                </div>
                                <div className="min-w-0 md:col-span-2">
                                    <label className="text-xs uppercase tracking-[0.16em] sm:tracking-[0.25em] text-zinc-500 font-mono mb-3 block">
                                        I'm interested in
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {interests.map((opt) => (
                                            <button
                                                type="button"
                                                key={opt}
                                                onClick={() => setForm({ ...form, interest: opt })}
                                                data-testid={`interest-${opt.toLowerCase()}`}
                                                className={`px-3 sm:px-4 py-2 rounded-full text-[11px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.2em] font-mono border transition ${
                                                    form.interest === opt
                                                        ? "bg-[#FF6B35] border-[#FF6B35] text-black"
                                                        : "border-white/15 text-zinc-300 hover:border-[#FF6B35]/60"
                                                }`}
                                            >
                                                {opt}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="min-w-0 md:col-span-2">
                                    <label className="text-xs uppercase tracking-[0.16em] sm:tracking-[0.25em] text-zinc-500 font-mono mb-2 block">
                                        Message
                                    </label>
                                    <textarea
                                        rows={4}
                                        value={form.message}
                                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                                        placeholder="Tell us what's on your mind..."
                                        data-testid="form-message"
                                        className="w-full min-w-0 bg-transparent border-b border-white/15 focus:border-[#FF6B35] outline-none py-3 text-sm sm:text-base text-white placeholder:text-zinc-600 transition resize-none"
                                    />
                                </div>
                            </div>

                            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div className="min-w-0 text-xs text-zinc-500 font-mono uppercase tracking-[0.14em] sm:tracking-[0.2em] [overflow-wrap:anywhere]">
                                    {status === "success" && (
                                        <span className="text-[#FF6B35] inline-flex items-center gap-2">
                                            <Check size={14} /> Email draft opened. Send it from your mail app.
                                        </span>
                                    )}
                                    {status === "idle" && "We never share your details. Promise."}
                                </div>
                                <button
                                    type="submit"
                                    data-testid="form-submit"
                                    className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#FF6B35] hover:bg-[#FF8957] disabled:opacity-60 text-black font-bold uppercase tracking-wider text-sm px-5 py-4 transition-all hover:scale-105 sm:w-auto sm:px-7"
                                >
                                    Send message <ArrowUpRight size={16} />
                                </button>
                            </div>
                        </motion.form>
                    </div>
                </div>
            </div>
        </section>
    );
}

export function Footer() {
    return (
        <footer className="relative border-t border-white/10 bg-[#0a0a0a]" data-testid="footer">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-16">
                {/* Massive footer text */}
                <div className="font-display font-black text-[14vw] md:text-[10vw] leading-none tracking-tighter text-stroke select-none pointer-events-none">
                    JAF · 2026
                </div>

                <div className="grid grid-cols-12 gap-8 mt-12 pt-12 border-t border-white/10">
                    <div className="col-span-12 md:col-span-5">
                        <img src={LOGO_URL} alt="JAF" className="h-12 mb-6" />
                        <p className="text-zinc-400 text-sm leading-relaxed max-w-sm">
                            {EVENT.tagline}. {EVENT.subline}
                        </p>
                    </div>

                    <div className="col-span-6 md:col-span-3">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#FF6B35] font-mono mb-4">
                            Festival
                        </div>
                        <ul className="space-y-2 text-sm text-zinc-300">
                            <li><a href="#about" className="hover:text-white">About</a></li>
                            <li><a href="#expect" className="hover:text-white">What to Expect</a></li>
                            <li><a href="#schedule" className="hover:text-white">Schedule</a></li>
                            <li><a href="#awards" className="hover:text-white">Awards</a></li>
                        </ul>
                    </div>

                    <div className="col-span-6 md:col-span-2">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#FF6B35] font-mono mb-4">
                            Partner
                        </div>
                        <ul className="space-y-2 text-sm text-zinc-300">
                            <li><a href="#sponsorships" className="hover:text-white">Sponsorship</a></li>
                            <li><a href="#branding" className="hover:text-white">Branding</a></li>
                            <li><a href="#contact" className="hover:text-white">Contact</a></li>
                        </ul>
                    </div>

                    <div className="col-span-12 md:col-span-2">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#FF6B35] font-mono mb-4">
                            Follow
                        </div>
                        <ul className="space-y-2 text-sm text-zinc-300">
                            {CONTACT.socials.map((s) => (
                                <li key={s.name}>
                                    <a href={s.url} className="hover:text-white inline-flex items-center gap-2">
                                        {s.name} <ArrowUpRight size={12} />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs uppercase tracking-[0.25em] text-zinc-500 font-mono">
                    <div>© 2026 Jaipur Ad Fest · Powered by RajRox</div>
                    <div>Made with obsession in Jaipur, India</div>
                </div>
            </div>
        </footer>
    );
}
